// pages/Oati/OattDashboardPage.tsx

import { useState, useEffect } from 'react';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@constants/pagination';
import { useOattSession } from '@hooks/useOattSession';
import { Table, Empty, Spin, Alert, Modal, Input, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { useQuery, useMutation } from '@apollo/client';
import { GET_OATT_DATA } from '@graphql/oatt.gql';
import { COMPUTE_OATT_DATA } from '@graphql/oattcomputation.gql';
import {
  CREATE_OATT_REVISION,
  GET_OATT_REVISION_NUMBERS,
} from '@graphql/oattrevision.gql';
import { useNavigate, Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { SyncOutlined, UploadOutlined } from '@ant-design/icons';
import PageLayout from '@components/layout/PageLayout';
import { NSPDatePicker } from '@components/ui/NSPDatepicker';
import styles from './OattPages.module.css';
import { NSPButton } from '@components/ui/NSPButton';
import useAuthRoles from '@hooks/useAuthRoles';
import { useOattCustomer } from '@hooks/useOattcustomer';
import oattBillingService from '@services/oattBillingService';

interface OattDataRecord {
  oatt_data_id: number;
  seller: string;
  aref: string;
  customer_code: string;
  billing_period: string;
  start_time: string;
  stop_time: string;
  mw_granted: number;
  billing_rate: number;
  billing_type: string;
  ts_class: string;
  increment: string;
  ts_type: string;
  ts_window: string;
  hours: number;
  net_trans: number;
  asr_rv_net: number;
  asr_sc_net: number;
  total_charges: number;
}

type ImportState =
  | { kind: 'idle' }
  | { kind: 'success'; message: string }
  | { kind: 'rowError'; message: string }
  | { kind: 'apiError'; errorMessage: string };

const columns: ColumnsType<OattDataRecord> = [
  {
    title: 'Service Seller',
    dataIndex: 'seller',
    key: 'seller',
    width: 120,
    sorter: (a, b) => a.seller.localeCompare(b.seller),
  },
  { title: 'Reservation Ref #', dataIndex: 'aref', key: 'aref', width: 160 },
  { title: 'Customer Code', dataIndex: 'customer_code', key: 'customer_code', width: 140 },
  { title: 'Billing Period', dataIndex: 'billing_period', key: 'billing_period', width: 130 },
  {
    title: 'MW Granted', dataIndex: 'mw_granted', key: 'mw_granted', align: 'right', width: 110,
    render: (v) => (v != null ? Number(v).toLocaleString() : '—'),
  },
  {
    title: 'Start Time', dataIndex: 'start_time', key: 'start_time', width: 160,
    render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—'),
  },
  {
    title: 'Stop Time', dataIndex: 'stop_time', key: 'stop_time', width: 160,
    render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—'),
  },
  { title: 'Billing Type', dataIndex: 'billing_type', key: 'billing_type', width: 120 },
  {
    title: 'Billing Rate', dataIndex: 'billing_rate', key: 'billing_rate', align: 'right', width: 120,
    render: (v) => (v != null ? `$${Number(v).toFixed(4)}` : '—'),
  },
  {
    title: 'Hours', dataIndex: 'hours', key: 'hours', align: 'right', width: 90,
    render: (v) => (v != null ? Number(v).toLocaleString() : '—'),
  },
  {
    title: 'Net Trans', dataIndex: 'net_trans', key: 'net_trans', align: 'right', width: 110,
    render: (v) => (v != null ? Number(v).toLocaleString() : '—'),
  },
  { title: 'TS Class', dataIndex: 'ts_class', key: 'ts_class', width: 100 },
  { title: 'Increment', dataIndex: 'increment', key: 'increment', width: 110 },
  { title: 'TS Type', dataIndex: 'ts_type', key: 'ts_type', width: 100 },
  { title: 'TS Window', dataIndex: 'ts_window', key: 'ts_window', width: 110 },
  {
    title: 'ASR RV Net', dataIndex: 'asr_rv_net', key: 'asr_rv_net', align: 'right', width: 120,
    render: (v) => (v != null ? `$${Number(v).toFixed(4)}` : '—'),
  },
  {
    title: 'ASR SC Net', dataIndex: 'asr_sc_net', key: 'asr_sc_net', align: 'right', width: 120,
    render: (v) => (v != null ? `$${Number(v).toFixed(4)}` : '—'),
  },
  {
    title: 'Total Charges', dataIndex: 'total_charges', key: 'total_charges', align: 'right', width: 130,
    render: (v) =>
      v != null ? (
        <strong>
          ${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </strong>
      ) : '—',
  },
];

const OattDashboardPage = () => {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const { isOattBillApprover, isOattRateOwner, isOattBillPreparer } = useAuthRoles();
  const { customerId, customerCode, CustomerSelect } = useOattCustomer();
  const { selectedDate, setSelectedDate } = useOattSession();
  const [alertInfo, setAlertInfo] = useState<{
    type: 'success' | 'error' | 'warning';
    msg: string;
  } | null>(null);

  // ── Import state ────────────────────────────────────────────────────────────
  const [importDate, setImportDate] = useState<Dayjs>(dayjs());
  const [provider, setProvider] = useState<string>('');
  const [importing, setImporting] = useState(false);
  const [importState, setImportState] = useState<ImportState>({ kind: 'idle' });

  useEffect(() => {
    oattBillingService.setMsalInstance(instance);
  }, [instance]);

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[OattDashboard] REACT_APP_ALLOW_FUTURE_DATES =', process.env.REACT_APP_ALLOW_FUTURE_DATES);
  }, []);

  const selectedMonth = selectedDate.month() + 1;
  const selectedYear = selectedDate.year();

  // ── Queries ────────────────────────────────────────────────────────────────
  const { data, loading, error } = useQuery<{ oattData: OattDataRecord[] }>(
    GET_OATT_DATA,
    {
      variables: { month: selectedMonth, year: selectedYear, customerId },
      // Skip until we have a customer selected
      skip: customerId === null,
      fetchPolicy: 'cache-and-network',
    },
  );

  const { data: revNumsData, refetch: refetchRevNums } = useQuery<{
    oattRevisionNumbers: number[];
  }>(
    GET_OATT_REVISION_NUMBERS,
    {
      variables: { month: selectedMonth, year: selectedYear, customerId },
      skip: customerId === null,
      fetchPolicy: 'cache-and-network',
    },
  );

  // ── Mutations ──────────────────────────────────────────────────────────────
  const [computeOattData, { loading: computing }] = useMutation(COMPUTE_OATT_DATA);
  const [createOattRevision, { loading: creatingRev }] = useMutation(CREATE_OATT_REVISION);
  const [isRevisionModalOpen, setIsRevisionModalOpen] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const oattData = data?.oattData ?? [];
  const revisionNums = revNumsData?.oattRevisionNumbers ?? [];
  const latestRevNo = revisionNums.length > 0 ? revisionNums[revisionNums.length - 1] : null;

  const pageTitle = `${customerCode ?? 'Select Customer'} - ${selectedDate.format('MMMM YYYY')} OATT Reservation Data`;

  // ── Import handler ─────────────────────────────────────────────────────────
  const handleImportReservations = async () => {
    setImporting(true);
    setImportState({ kind: 'idle' });

    try {
      const result = await oattBillingService.importReservations({
        billing_month: importDate.month() + 1,
        billing_year: importDate.year(),
        ...(provider.trim() && { provider: provider.trim().toUpperCase() }),
      });

      const monthLabel = importDate.format('MMMM YYYY');
      const providerLabel = `${result.providersProcessed} provider${result.providersProcessed !== 1 ? 's' : ''}`;
      const customerSuffix = result.customersCreated
        ? `, ${result.customersCreated} new customer${result.customersCreated !== 1 ? 's' : ''} registered`
        : '';
      const summary =
        `${providerLabel} processed — ` +
        `${result.rowsInserted} inserted, ${result.rowsUpdated} updated${customerSuffix}.`;

      if (result.errors?.length) {
        const errorLines = result.errors
          .map((e) =>
            [e.provider && `[${e.provider}]`, e.aref && `AREF ${e.aref}`, e.reason]
              .filter(Boolean)
              .join(' ')
          )
          .join('\n');
        setImportState({
          kind: 'rowError',
          message: `${monthLabel} import completed with errors — ${summary}\n${errorLines}`,
        });
      } else {
        setImportState({
          kind: 'success',
          message: `Billing reservations for ${monthLabel} imported from ${result.mode} — ${summary}`,
        });
      }
    } catch (err: any) {
      if (err?.oattError) {
        setImportState({
          kind: 'apiError',
          errorMessage: err.errorMessage || 'Unknown OATT API error.',
        });
        return;
      }
      const message =
        err?.error || err?.message || 'Failed to import billing reservations. Please try again.';
      setImportState({ kind: 'rowError', message });
    } finally {
      setImporting(false);
    }
  };

  // ── Compute handler ────────────────────────────────────────────────────────
  const handleCompute = async () => {
    setAlertInfo(null);
    try {
      const res = await computeOattData({
        variables: { month: selectedMonth, year: selectedYear, customerId },
      });
      const result = res.data?.computeOattData;
      if (result?.success) {
        setAlertInfo({
          type: 'success',
          msg: `Computation successful — ${result.count} record(s) computed for ${selectedDate.format('MMMM YYYY')}`,
        });
        setTimeout(() => navigate('/oati/computation'), 2000);
      } else {
        setAlertInfo({
          type: 'warning',
          msg: result?.message ?? 'No records were computed. Please check the source data.',
        });
      }
    } catch (err: any) {
      setAlertInfo({
        type: 'error',
        msg: `Computation failed: ${err?.message ?? 'An unexpected error occurred.'}`,
      });
    }
  };

  // ── Create Revision handler ────────────────────────────────────────────────
  const handleCreateRevision = async () => {
    setIsRevisionModalOpen(false);
    setAlertInfo(null);

    try {
      const res = await createOattRevision({
        variables: {
          month: selectedMonth,
          year: selectedYear,
          customerId,
        },
      });

      const result = res.data?.createOattRevision;

      if (result?.success) {
        setAlertInfo({
          type: 'success',
          msg: result.message ?? `Revision ${result.revision_no} created.`,
        });

        refetchRevNums();

        navigate(
          `/oati/revision?month=${selectedMonth}&year=${selectedYear}&revisionNo=${result.revision_no}&customerId=${customerId}`
        );
      } else {
        setAlertInfo({
          type: 'warning',
          msg:
            result?.message ??
            'Could not create revision. No original records found.',
        });
      }
    } catch (err: any) {
      setAlertInfo({
        type: 'error',
        msg: `Failed to create revision: ${err?.message ?? 'Unexpected error.'}`,
      });
    }
  };


  return (
    <PageLayout
      title={pageTitle}
      headerAccessoryView={
        <div className={styles.periodSelector} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* ── Customer dropdown — seamlessly replaces the static label ── */}
          <div className={styles.customerSelectWrapper}>
            <CustomerSelect />
          </div>
          <NSPDatePicker
            picker="month"
            format="MMM YYYY"
            allowClear={false}
            value={selectedDate}
            onChange={(date) => date && setSelectedDate(date)}
            disabledDate={
              process.env.REACT_APP_ALLOW_FUTURE_DATES === 'true'
                ? undefined
                : (current) => current && current >= dayjs().startOf('month')
            }
          />
        </div>
      }
    >
      {alertInfo && (
        <Alert
          type={alertInfo.type}
          message={alertInfo.msg}
          showIcon
          closable
          onClose={() => setAlertInfo(null)}
          className={`${styles.alertBanner} ${alertInfo.type === 'success'
            ? styles.alertSuccess
            : alertInfo.type === 'warning'
              ? styles.alertWarning
              : styles.alertError
            }`}
        />
      )}

      {/* ── IMPORT DATA section ──────────────────────────────────────────── */}
      <div className={styles.content}>
        <div className={styles.billingFetchSection}>
          <div className={styles.billingFetchHeader}>
            <span className={styles.billingFetchTitle}>IMPORT DATA</span>
          </div>

          {importState.kind === 'apiError' ? (
            <Alert
              type="error"
              showIcon
              message="Unable to Retrieve OATT Data"
              description={
                <>
                  <p style={{ margin: '4px 0' }}>
                    The error message is: <strong>{importState.errorMessage}</strong>
                  </p>
                  <p style={{ margin: '4px 0' }}>
                    You can{' '}
                    <Button
                      type="link"
                      style={{ padding: 0, height: 'auto', fontSize: 'inherit' }}
                      onClick={() => setImportState({ kind: 'idle' })}
                    >
                      try again later
                    </Button>
                    , or use the <Link to="/oati/upload">file upload page</Link>.
                  </p>
                </>
              }
            />
          ) : (
            <>
              {importState.kind !== 'idle' && (
                <Alert
                  type={importState.kind === 'success' ? 'success' : 'error'}
                  message={importState.message}
                  showIcon
                  closable
                  onClose={() => setImportState({ kind: 'idle' })}
                  className={styles.fetchAlert}
                  style={{ whiteSpace: 'pre-line' }}
                />
              )}

              <div className={styles.billingFetchControls}>
                <Input
                  placeholder="Provider (e.g. NPPO)"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                  style={{ width: 180 }}
                  disabled={importing || isOattBillApprover}
                  allowClear
                />
                <NSPDatePicker
                  picker="month"
                  format="MMMM YYYY"
                  allowClear={false}
                  value={importDate}
                  onChange={(date) => {
                    if (date) {
                      setImportDate(date);
                      setImportState({ kind: 'idle' });
                    }
                  }}
                  style={{ width: 180 }}
                  disabled={importing || isOattBillApprover}
                />
                <NSPButton
                  icon={<SyncOutlined />}
                  onClick={handleImportReservations}
                  loading={importing}
                  disabled={importing || isOattBillApprover}
                >
                  {importing ? 'Importing...' : 'Import Billing Reservations'}
                </NSPButton>

                <NSPButton
                  icon={<UploadOutlined />}
                  onClick={() => navigate('/oati/upload')}
                  disabled={isOattBillApprover}
                >
                  File Upload
                </NSPButton>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Meter data table ─────────────────────────────────────────────── */}
      <div className={styles.content}>
        {error ? (
          <Empty description={`Error loading data: ${error.message}`} />
        ) : (
          <Table<OattDataRecord>
            className={styles.oattTable}
            dataSource={oattData.map((r) => ({ ...r, key: r.oatt_data_id }))}
            columns={columns}
            loading={loading ? { indicator: <Spin size="large" /> } : false}
            rowKey="oatt_data_id"
            pagination={{
              pageSize,
              showTotal: (total) => `Total ${total} records`,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              onShowSizeChange: (_, size) => setPageSize(size),
            }}
            scroll={{ x: 'max-content', y: 'calc(100vh - 380px)' }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    customerId === null
                      ? 'Select a customer above'
                      : `No OATT data found for ${customerCode} — ${selectedDate.format('MMMM YYYY')}`
                  }
                />
              ),
            }}
          />
        )}
      </div>

      <div className={styles.computeButtonContainer}>
        {latestRevNo !== null && (
          <NSPButton
            size="small"
            style={{ marginRight: 8 }}
            onClick={() =>
              navigate(
                `/oati/revision?month=${selectedMonth}&year=${selectedYear}&revisionNo=${latestRevNo}&customerId=${customerId}`,
              )
            }
          >
            {isOattBillPreparer ? 'Edit Latest Revision' : 'View Latest Revision'}
          </NSPButton>
        )}

        <NSPButton
          onClick={() => setIsRevisionModalOpen(true)}
          loading={creatingRev}
          disabled={oattData.length === 0 || isOattBillApprover || isOattRateOwner || customerId === null}
          style={{ marginRight: 8 }}
        >
          Create Revision
        </NSPButton>
        {latestRevNo === null && (
          <NSPButton
            onClick={handleCompute}
            loading={computing}
            disabled={oattData.length === 0 || customerId === null || isOattBillApprover}
          >
            QC Compute OATT
          </NSPButton>
        )}
      </div>
      <Modal
        title="Create Revision"
        open={isRevisionModalOpen}
        onOk={handleCreateRevision}
        onCancel={() => setIsRevisionModalOpen(false)}
        okText="Yes, Create"
        cancelText="Cancel"
        centered
        width={400}
      >
        <p>
          Are you sure you want to create a revision for{' '}
          <strong>{customerCode}</strong> -{' '}
          <strong>{selectedDate.format('MMMM YYYY')}</strong>?
        </p>
      </Modal>
    </PageLayout>
  );
};

export default OattDashboardPage;
